// src/hooks/useShareStats.ts
//
// Live view-count stats for a shared card. The sharer can see how
// many people have looked at their share. Independent from card
// state — the card may not even exist in shared_cards/ yet (the
// publish happens lazily on first share). Returns nulls until/unless
// data is available.

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

export interface ShareStats {
  viewCount: number;
  uniqueViewCount: number;
  lastViewedAt: Date | null;
  exists: boolean;
}

/** Live snapshot of /shared_cards/{shareId} view fields. Returns
 *  null if not yet shared (doc doesn't exist). */
export function useShareStats(shareId: string | null | undefined): ShareStats | null {
  const [stats, setStats] = useState<ShareStats | null>(null);

  useEffect(() => {
    if (!shareId) {
      setStats(null);
      return;
    }
    const ref = doc(db, 'shared_cards', shareId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setStats({
            viewCount: 0,
            uniqueViewCount: 0,
            lastViewedAt: null,
            exists: false,
          });
          return;
        }
        const data = snap.data() as any;
        const lastViewed = data?.last_viewed_at;
        setStats({
          viewCount: typeof data?.view_count === 'number' ? data.view_count : 0,
          uniqueViewCount:
            typeof data?.unique_view_count === 'number'
              ? data.unique_view_count
              : 0,
          lastViewedAt:
            lastViewed && typeof lastViewed.toDate === 'function'
              ? lastViewed.toDate()
              : null,
          exists: true,
        });
      },
      (err) => {
        console.warn('useShareStats listener error:', err);
        setStats(null);
      }
    );
    return unsub;
  }, [shareId]);

  return stats;
}
